import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, MapPin, Phone, Mail, Hash, HardHat,
  Edit2, ShoppingCart, ChevronRight,
} from 'lucide-react';
import {
  clients, commandes,
  formatDate, formatMontant,
} from '../data/mockData';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = clients.find((c) => c.id === id);

  if (!client) {
    return (
      <div className="page-container">
        <button className="back-link" onClick={() => navigate('/clients')}>
          <ArrowLeft size={16} /> Retour aux clients
        </button>
        <div className="empty-state">
          <Briefcase size={28} />
          <p>Client introuvable.</p>
        </div>
      </div>
    );
  }

  const commandesClient = commandes.filter((c) => c.clientId === client.id);
  const totalCommandesHT = commandesClient.reduce((sum, c) => sum + c.montantHT, 0);

  return (
    <div className="page-container">
      <button className="back-link" onClick={() => navigate('/clients')}>
        <ArrowLeft size={16} /> Retour aux clients
      </button>

      <div className="page-header">
        <div>
          <div className="page-title-row">
            <div className="page-icon-wrapper">
              <Briefcase size={22} />
            </div>
            <div>
              <h1 className="page-title">{client.nom}</h1>
              <p className="page-subtitle mono">{client.codeMarche}</p>
            </div>
          </div>
        </div>
        <div className="page-actions">
          <span className="badge badge-info">{client.typeMarche}</span>
          <button className="btn-ghost"><Edit2 size={14} /> Modifier</button>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card">
          <div className="kpi-icon"><HardHat size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Chantiers</span>
            <span className="kpi-value">{client.chantiers.length}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><ShoppingCart size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Commandes associees</span>
            <span className="kpi-value">{commandesClient.length}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><Hash size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Chantiers livres</span>
            <span className="kpi-value">
              {client.chantiers.filter((c) => c.statut === 'livre').length}
            </span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><Briefcase size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Volume total HT</span>
            <span className="kpi-value">{formatMontant(totalCommandesHT)}</span>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: '1.25rem' }}>
        <div className="card">
          <header className="card-header">
            <div className="card-title">
              <Briefcase size={18} />
              <h2>Fiche client</h2>
            </div>
          </header>
          <div className="card-body info-grid">
            <div>
              <span className="info-label"><Hash size={12} /> Code marche</span>
              <span className="info-value mono">{client.codeMarche}</span>
            </div>
            <div>
              <span className="info-label">Type de marche</span>
              <span className="info-value">{client.typeMarche}</span>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <span className="info-label"><MapPin size={12} /> Adresse</span>
              <span className="info-value">{client.adresse}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <header className="card-header">
            <div className="card-title">
              <Mail size={18} />
              <h2>Contact principal</h2>
            </div>
          </header>
          <div className="card-body info-grid">
            <div>
              <span className="info-label">Nom</span>
              <span className="info-value">{client.contact}</span>
            </div>
            <div>
              <span className="info-label"><Mail size={12} /> Email</span>
              <span className="info-value">{client.email}</span>
            </div>
            <div>
              <span className="info-label"><Phone size={12} /> Telephone</span>
              <span className="info-value">{client.telephone}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <HardHat size={18} />
            <h2>Chantiers rattaches ({client.chantiers.length})</h2>
          </div>
        </header>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Chantier</th>
                <th>Statut</th>
                <th>Avancement</th>
              </tr>
            </thead>
            <tbody>
              {client.chantiers.map((ch) => (
                <tr key={ch.id}>
                  <td><span className="mono">{ch.id}</span></td>
                  <td>{ch.nom}</td>
                  <td>
                    <span className={`badge badge-${ch.statut === 'livre' ? 'ok' : ch.statut === 'etude' ? 'warn' : 'info'}`}>
                      {ch.statut === 'livre' ? 'Livre' : ch.statut === 'etude' ? 'En etude' : 'En cours'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="progress" style={{ width: 140 }}>
                        <div className="progress-bar" style={{ width: `${ch.avancement}%` }} />
                      </div>
                      <span className="mono">{ch.avancement}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <ShoppingCart size={18} />
            <h2>Commandes liees ({commandesClient.length})</h2>
          </div>
        </header>
        <div className="card-body">
          {commandesClient.length === 0 ? (
            <div className="empty-state compact">
              <p>Aucune commande n est associee a ce client.</p>
            </div>
          ) : (
            <table className="data-table clickable">
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Date</th>
                  <th>Fournisseur</th>
                  <th>Chantier</th>
                  <th style={{ textAlign: 'right' }}>Montant HT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {commandesClient.map((cmd) => (
                  <tr key={cmd.id} className="hoverable clickable" onClick={() => navigate(`/commandes/${cmd.id}`)}>
                    <td><span className="mono">{cmd.id}</span></td>
                    <td>{formatDate(cmd.date)}</td>
                    <td>{cmd.fournisseurNom}</td>
                    <td>{cmd.chantier}</td>
                    <td style={{ textAlign: 'right' }}><strong>{formatMontant(cmd.montantHT)}</strong></td>
                    <td style={{ width: 28 }}><ChevronRight size={16} className="arrow-muted" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
